// Utility functions

// Limits
export const MAX_IMAGE_BYTES = 10000000; // ~10 MB per embedded non-GIF image
export const MAX_GIF_BYTES = 10000000;  // ~10 MB per embedded GIF to preserve animation

export const dataUrlToBytes = (dataUrl) => {
  if (!dataUrl) return 0;
  const comma = dataUrl.indexOf(',');
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  // Base64 size calculation
  let bytes = Math.ceil((b64.length * 3) / 4);
  if (b64.endsWith('==')) bytes -= 2; else if (b64.endsWith('=')) bytes -= 1;
  return bytes;
};

// Sanitize question rich text while allowing only basic formatting tags
export function sanitizeRichText(input) {
  if (!input) return '';
  let s = String(input);
  // Strip attributes from allowed tags
  s = s.replace(/<(b|i|u)\b[^>]*>/gi, '<$1>');
  // Normalize br
  s = s.replace(/<br\s*\/?>/gi, '<br/>');
  // Convert block-level elements (inserted by contenteditable on Enter) into line breaks.
  // Opening tags become the line break (since the block starts a new line), closings drop.
  s = s.replace(/<(p|div|li|h[1-6])\b[^>]*>/gi, '<br/>');
  s = s.replace(/<\/(p|div|li|h[1-6])\s*>/gi, '');
  // Collapse 3+ consecutive line breaks to a max of 2 (avoid huge gaps from nested divs)
  s = s.replace(/(?:<br\/>\s*){3,}/gi, '<br/><br/>');
  // Trim leading and trailing <br/> that contenteditable often leaves around content
  s = s.replace(/^(?:\s*<br\/>)+/i, '');
  s = s.replace(/(?:<br\/>\s*)+$/i, '');
  // Remove all other tags
  s = s.replace(/<(?!\/?(b|i|u|br)\b)[^>]*>/gi, '');
  return s;
}

// File to data URL conversion
export const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  if (!file) { resolve(null); return; }
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Image compression utility
export const compressImageFile = async (file, maxDimension = 1920, quality = 0.9) => {
  try {
    const dataUrl = await fileToDataUrl(file);
    if (!dataUrl) return null;
    // Preserve GIFs to keep animation (no canvas recompress)
    if (/\.gif$/i.test(file.name) || dataUrl.startsWith('data:image/gif')) {
      return dataUrl;
    }
    const img = new Image();
    const imgLoaded = new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    img.src = dataUrl;
    await imgLoaded;
    let { width, height } = img;
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    // Prefer WEBP; fallback to PNG to keep transparent PNG
    let out = canvas.toDataURL('image/webp', quality);
    if (!out || out.length >= dataUrl.length) {
      out = canvas.toDataURL('image/png');
    }
    return out;
  } catch (e) {
    console.error('Bildkomprimierung fehlgeschlagen, verwende Original.', e);
    return await fileToDataUrl(file);
  }
};
